import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { ChevronDown, Facebook, Instagram, MessageCircle } from "lucide-react";
import { SITE } from "@/config/site";
import { BookButton } from "@/components/GoldButton";
import { StarPattern } from "@/components/Ornament";
import { scrollToId } from "@/lib/scroll";
import { trackEvent, trackOutbound, withUtm } from "@/lib/analytics";
import "@/styles/theme-v2.css";

// ============================================================================
// HERO V2 — "Next level" pass on the live Hero.jsx.
// Same content, same data, same analytics wiring, same section anchors —
// nothing here is invented. What changes is craft: a slow gold light-sweep
// across the poster frame, a second ghost-arch layer for depth behind the
// Urdu watermark, a hairline accent that finally puts the brand's own
// (documented, never-used) crimson to work, and an editorial "Chapter 00"
// eyebrow that ties the hero into the same numbered-chapter language the
// rest of the site already uses (Performers is 03, Details is 04, etc.).
// ============================================================================

const EASE = [0.16, 1, 0.3, 1];

const SOCIALS = [
  {
    key: "instagram",
    label: "Instagram",
    aria: "Follow us on Instagram",
    url: SITE.instagramUrl,
    event: "instagram_click",
    Icon: Instagram,
  },
  {
    key: "facebook",
    label: "Facebook",
    aria: "Follow us on Facebook",
    url: SITE.facebookUrl,
    event: "facebook_click",
    Icon: Facebook,
  },
  {
    key: "whatsapp",
    label: "WhatsApp Channel",
    aria: "Join our WhatsApp Channel",
    url: SITE.whatsappChannelUrl,
    event: "whatsapp_click",
    Icon: MessageCircle,
  },
];

function MaskedLine({ children, delay, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.2, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function StatusChip({ children, pulse = false, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/35 bg-[#D4AF37]/[0.07] px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#F3E5AB] sm:text-sm"
    >
      {pulse && (
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#E6CA65]" aria-hidden="true" />
      )}
      {children}
    </span>
  );
}

export function HeroV2() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 55, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 55, damping: 16 });
  const { scrollY } = useScroll();
  const archY = useTransform(scrollY, [0, 800], [0, 120]);
  const urduY = useTransform(scrollY, [0, 800], [0, -60]);

  const onMouseMove = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const meta = [
    { label: "Date", value: SITE.date, testId: "hero-event-date" },
    { label: "Show", value: SITE.schedule.show, testId: "hero-event-time" },
    { label: "Venue", value: SITE.venue.name, testId: "hero-event-venue" },
    { label: "City", value: SITE.city.toUpperCase(), testId: "hero-event-city" },
  ];

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-center overflow-hidden px-5 pb-24 pt-32 sm:px-8 lg:pt-36"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-[#D4AF37]/[0.09] blur-[120px]" />
        <div className="absolute bottom-0 right-0 h-[400px] w-[520px] rounded-full bg-[#AA7C11]/[0.07] blur-[100px]" />
        {/* new: a third, much fainter emerald glow for depth in the far corner —
            the only place emerald appears in the hero, kept almost subliminal */}
        <div className="absolute bottom-24 left-0 h-[320px] w-[420px] rounded-full bg-[#0D3B2E]/[0.10] blur-[110px]" />
        {/* new: a second, fainter arch silhouette behind the big Urdu ghost type,
            so the watermark reads as layered artwork rather than flat text */}
        <svg
          className="absolute -right-24 top-8 h-[520px] w-[520px] opacity-[0.05] lg:h-[640px] lg:w-[640px]"
          viewBox="0 0 200 200"
          fill="none"
        >
          <path
            d="M20 190 V90 C20 40 60 12 100 12 C140 12 180 40 180 90 V190"
            stroke="#D4AF37"
            strokeWidth="1.4"
          />
        </svg>
      </div>

      <motion.p
        dir="rtl"
        lang="ur"
        aria-hidden="true"
        style={reduce ? {} : { y: urduY }}
        className="font-urdu pointer-events-none absolute -right-8 top-20 select-none text-[26vw] leading-none text-[#D4AF37]/[0.07] lg:text-[15rem]"
      >
        {SITE.urdu}
      </motion.p>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.img
            src={SITE.brand.logo}
            alt={SITE.brand.logoAlt}
            data-testid="hero-logo"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.05, ease: EASE }}
            className="mb-8 h-20 w-auto sm:h-24"
          />

          {/* new: editorial "Chapter 00" eyebrow — ties the hero into the same
              numbered-chapter language ChapterHeading gives every later section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.05, ease: EASE }}
            className="mb-4 flex items-center gap-4"
          >
            <span className="font-display text-lg text-[#D4AF37]">00</span>
            <span className="h-px w-12 bg-gradient-to-r from-[#D4AF37]/60 to-transparent" aria-hidden="true" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37]/90 sm:text-sm">
              Chapter 00 — The Invitation
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] px-5 py-2.5 backdrop-blur-sm"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" aria-hidden="true" />
            <span className="text-xs font-bold uppercase tracking-[0.28em] text-[#E6CA65] sm:text-sm">
              {SITE.taglineShort}
            </span>
          </motion.div>

          <h1 className="font-display text-[16.5vw] leading-[0.98] tracking-tight text-[#F7F4EF] sm:text-[12vw] lg:text-[5.6rem] xl:text-[6.6rem]">
            <MaskedLine delay={0.3}>CHITTI</MaskedLine>
            <MaskedLine delay={0.44} className="gold-text">
              AYEE HAI
            </MaskedLine>
          </h1>

          {/* new: a thin hairline that grows in under the tagline, gold fading to
              the brand's own crimson — the first real, deliberate use of that
              documented accent color anywhere on the site */}
          <div
            className="cah-hairline-grow mt-6 h-[2px] w-24"
            style={{ background: "linear-gradient(90deg, #D4AF37, #7A1B2B)" }}
            aria-hidden="true"
          />

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.62, ease: EASE }}
            className="font-accent mt-5 max-w-xl text-lg italic leading-relaxed text-[#C8C2B7] sm:text-xl"
          >
            {SITE.tagline} — live in the heart of Bengaluru.
          </motion.p>

          <motion.dl
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.76, ease: EASE }}
            className="mt-10 grid grid-cols-2 gap-x-6 gap-y-7 border-t border-[#D4AF37]/15 pt-8 sm:grid-cols-4"
          >
            {meta.map((m, i) => (
              <div
                key={m.label}
                data-testid={m.testId}
                className={i > 0 ? "sm:border-l sm:border-[#D4AF37]/15 sm:pl-6" : ""}
              >
                <dt className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#D4AF37]">
                  {m.label}
                </dt>
                <dd className="font-display mt-2 text-base leading-snug text-[#F7F4EF] sm:text-lg">
                  {m.value}
                </dd>
              </div>
            ))}
          </motion.dl>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.86, ease: EASE }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <StatusChip pulse testId="hero-status-filling-fast">
              {SITE.listingStatus}
            </StatusChip>
            <StatusChip testId="hero-status-interested">
              {SITE.interestedCount} interested on BookMyShow
            </StatusChip>
            <StatusChip testId="hero-status-price">From ₹{SITE.priceFrom}</StatusChip>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.98, ease: EASE }}
            className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <BookButton location="hero" label="Tickets Are Live" testId="hero-book-tickets-btn" />
            <a
              href="#experience"
              data-testid="hero-explore-btn"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#experience");
              }}
              className="inline-flex min-h-[56px] items-center justify-center gap-3 rounded-full border border-[#D4AF37]/40 px-10 text-base font-semibold uppercase tracking-[0.12em] text-[#F3E5AB] transition-colors duration-300 hover:bg-[#D4AF37]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E6CA65] sm:text-lg"
            >
              Explore the Event
              <ChevronDown className="h-5 w-5" aria-hidden="true" />
            </a>
            <a
              href="/sponsor"
              data-testid="hero-sponsor-btn"
              className="inline-flex min-h-[56px] items-center justify-center gap-3 rounded-full border border-[#D4AF37]/40 px-10 text-base font-semibold uppercase tracking-[0.12em] text-[#F3E5AB] transition-colors duration-300 hover:bg-[#D4AF37]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E6CA65] sm:text-lg"
            >
              Become a Sponsor
            </a>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-4 text-sm text-[#918A7E]"
          >
            Official tickets exclusively on BookMyShow
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2, ease: EASE }}
            className="mt-6 flex flex-wrap items-center gap-3"
            aria-label="Official social channels"
          >
            {SOCIALS.map((s) => (
              <a
                key={s.key}
                href={withUtm(s.url)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.aria}
                data-testid={`hero-social-${s.key}`}
                onClick={() => {
                  trackEvent(s.event);
                  trackOutbound(s.key);
                }}
                className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#C8C2B7] transition-colors duration-300 hover:border-[#D4AF37]/60 hover:text-[#F3E5AB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E6CA65]"
              >
                <s.Icon className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" />
                {s.label}
              </a>
            ))}
          </motion.div>

          {SITE.posterAsset && (
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 1.15, ease: EASE }}
              className="mt-12 lg:hidden"
            >
              <div className="cah-light-sweep arch-frame relative mx-auto aspect-[3/4.2] w-full max-w-[290px] overflow-hidden border border-[#D4AF37]/35 bg-[#0E0C0A]">
                <img
                  src={SITE.posterAsset}
                  alt={`Official ${SITE.eventName} poster`}
                  data-testid="hero-poster-mobile"
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="eager"
                />
              </div>
            </motion.div>
          )}
        </div>

        <div className="hidden lg:col-span-5 lg:flex lg:justify-end">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.3, delay: 0.5, ease: EASE }}
            className="relative"
          >
            <motion.div
              style={reduce ? {} : { rotateX, rotateY, transformPerspective: 1000, y: archY }}
              className="cah-light-sweep arch-frame relative aspect-[3/4.2] w-[360px] overflow-hidden border border-[#D4AF37]/35 bg-[#0E0C0A] xl:w-[420px]"
            >
              {SITE.posterAsset ? (
                <img
                  src={SITE.posterAsset}
                  alt={`Official ${SITE.eventName} poster`}
                  data-testid="hero-poster"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <>
                  <StarPattern id="hero-arch-pattern" opacity={0.32} />
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_22%,rgba(212,175,55,0.22),transparent_65%)]" />
                  <div className="absolute inset-x-0 top-[17%] text-center">
                    <p
                      dir="rtl"
                      lang="ur"
                      className="font-urdu text-5xl leading-relaxed text-[#E6CA65] xl:text-6xl"
                      style={{ textShadow: "0 0 44px rgba(212,175,55,0.45)" }}
                    >
                      {SITE.urdu}
                    </p>
                  </div>
                  <div className="absolute inset-x-8 bottom-10 text-center">
                    <div className="mx-auto mb-4 h-px w-16 bg-[#D4AF37]/50" aria-hidden="true" />
                    <p className="font-display text-sm uppercase tracking-[0.35em] text-[#C8C2B7]">
                      19.09.2026 · Bengaluru
                    </p>
                  </div>
                </>
              )}
            </motion.div>
            {/* new: an inner gold hairline inset, just inside the frame edge, for a
                "jewel case" double-border feel instead of a single flat border */}
            <div
              className="arch-frame pointer-events-none absolute inset-[10px] border border-[#F3E5AB]/15"
              aria-hidden="true"
            />
            <div
              className="arch-frame absolute -left-4 top-4 -z-10 h-full w-full border border-[#D4AF37]/15"
              aria-hidden="true"
            />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-7 left-1/2 z-10 -translate-x-1/2 text-center"
        aria-hidden="true"
      >
        <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.4em] text-[#918A7E]">Scroll</p>
        <div className="scroll-line mx-auto h-10 w-px bg-[#D4AF37]/40" />
      </motion.div>
    </section>
  );
}
